import { Button } from '@heroui/react';
import React, { useEffect } from 'react';
import { FiX } from 'react-icons/fi';

export interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
}

const sizeClasses = {
  sm: 'w-96',
  md: 'w-[500px]',
  lg: 'w-[600px]',
  xl: 'w-[800px]',
  '2xl': 'w-[1000px]',
};

export const SidePanel: React.FC<SidePanelProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'xl',
  className = '',
}) => {
  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[2000] transition-opacity duration-300"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Side Panel */}
      <div
        className={`
          fixed top-0 right-0 h-full ${sizeClasses[size]}
          bg-content1 shadow-2xl z-[2001]
          flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          ${className}
        `}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'side-panel-title' : undefined}
        style={{ display: isOpen ? 'flex' : 'none' }}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between p-6 border-b border-divider">
            {title && (
              <h2
                id="side-panel-title"
                className="text-xl font-semibold text-foreground"
              >
                {title}
              </h2>
            )}
            {onClose && (
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={onClose}
                aria-label="Close panel"
                className="text-default-500 hover:text-danger"
              >
                <FiX size={20} />
              </Button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </>
  );
};
